import { useState, useRef, useEffect, memo, useCallback } from "react";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Plus, GripVertical, Trash2, Check, X, ListChecks, Shield, FileEdit } from "lucide-react";
import { useSwivuChecklistItems } from "@/react/hooks/entityHooks/useSwivuChecklistItems";
import { cn } from "@/modern/utils";
import { useChecklistItemsData } from "@/react/hooks/entityHooks/useChecklistItemsData";
import { useChecklistItemDragDrop } from "@/react/hooks/entityHooks/useChecklistItemDragDrop";
import { PermissionsSection } from "@/react/components/entityViews/checklist/components/PermissionsSection";
import { TComboSelectedValue } from "@/modern/components/ui/combobox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { motion } from "motion/react";
import { InputGroup, InputGroupInput, InputGroupButton, InputGroupAddon } from "@/modern/components/ui/input-group";
import { Label } from "@/modern/components/ui/label";
import { useEntityChecklistContext } from "@/react/components/entityViews/checklist/context/EntityChecklistContext";

interface ChecklistAddItemsModalProps {
  checklist: Checklist;
}

interface AddChecklistItemFormProps {
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const AddChecklistItemForm = memo(({ inputRef, value, onChange, onSubmit }: AddChecklistItemFormProps) => {
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  return (
    <form onSubmit={onSubmit} className="pb-2">
      <InputGroup>
        <InputGroupInput
          ref={inputRef}
          placeholder="Add new checklist item..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="py-4 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ring-0"
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton type="submit" variant="outline" size="sm">
            <Plus />
            Add item
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
});
AddChecklistItemForm.displayName = "AddChecklistItemForm";

/**
 * Custom hook to manage all checklist modal state and logic
 */
const useChecklistModal = (checklist: Checklist, setTab: (tab: string) => void) => {
  // Determine if this is create mode (no ID yet)
  const isCreateMode = !checklist.title;

  // State
  const [checklistItemTitle, setChecklistItemTitle] = useState("");
  const [editingItemId, setEditingItemId] = useState<number | string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [checklistTitle, setChecklistTitle] = useState(checklist.title || "");
  const [droppedItemId, setDroppedItemId] = useState<number | string | null>(null);
  const [visualItems, setVisualItems] = useState<ChecklistItems[]>([]);
  const [isTitleSaved, setIsTitleSaved] = useState(!isCreateMode);

  // Track checklist ID separately from prop to ensure hooks react to ID changes from phantom -> real
  const [currentChecklistId, setCurrentChecklistId] = useState<number | string | undefined>(checklist.id);
  const [isWaitingForSync, setIsWaitingForSync] = useState(false);

  // Track permission values in state to ensure UI updates when phantom record is created
  const [assignedBoardUsers, setAssignedBoardUsers] = useState<number[]>(checklist.assignedBoardUsers || []);
  const [canEdit, setCanEdit] = useState<number | null | undefined>(checklist.canEdit);
  const [canView, setCanView] = useState<number | null | undefined>(checklist.canView);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const savedScrollPositionRef = useRef<number>(0);
  const phantomChecklistRef = useRef<Checklist | null>(null);

  // Hooks
  const { createChecklistItem, deleteChecklistItem, updateChecklistItem } = useSwivuChecklistItems();
  const { updateChecklist, createChecklist, checklistStore } = useEntityChecklistContext();
  const { items } = useChecklistItemsData(currentChecklistId as number);
  const { draggedItem, dragOverItem, handleDragStart, handleDragOver, handleDragEnd: originalHandleDragEnd } = useChecklistItemDragDrop(items, scrollContainerRef);

  // Computed: determine if other tabs can be accessed
  const canAccessOtherTabs = !isCreateMode || (checklistTitle.trim().length > 2 && isTitleSaved && !isWaitingForSync);

  // Helpers
  const hasOrderChanged = (arr1: ChecklistItems[], arr2: ChecklistItems[]) => {
    if (arr1.length !== arr2.length) return true;
    return arr1.some((item, index) => item.id !== arr2[index].id);
  };

  const isPhantomId = (id: number | string | undefined): boolean => {
    return !id || String(id).startsWith("_generated");
  };

  // Wrap drag end to track dropped item
  const handleDragEnd = () => {
    if (draggedItem) {
      setDroppedItemId(draggedItem.id);
      setTimeout(() => setDroppedItemId(null), 400);
    }
    originalHandleDragEnd();
  };

  // Update visual order during active drag
  useEffect(() => {
    if (draggedItem && dragOverItem && draggedItem.id !== dragOverItem.id) {
      const draggedIndex = items.findIndex((item) => item.id === draggedItem.id);
      const targetIndex = items.findIndex((item) => item.id === dragOverItem.id);

      const reordered = [...items];
      const [removed] = reordered.splice(draggedIndex, 1);
      reordered.splice(targetIndex, 0, removed);

      setVisualItems(reordered);
    }
  }, [draggedItem, dragOverItem, items]);

  // Save scroll position when drag starts
  useEffect(() => {
    if (draggedItem && scrollContainerRef.current) {
      savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
  }, [draggedItem]);

  // Sync with items when order actually changes - only if not dragging
  useEffect(() => {
    if (!draggedItem && !dragOverItem && hasOrderChanged(visualItems, items)) {
      setVisualItems(items);

      // Restore scroll position after layout completes
      if (scrollContainerRef.current && savedScrollPositionRef.current > 0) {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = savedScrollPositionRef.current;
            savedScrollPositionRef.current = 0;
          }
        });
      }
    }
  }, [items, draggedItem, dragOverItem]);

  // Sync permission state when checklist prop changes (for existing checklists)
  useEffect(() => {
    if (!isCreateMode) {
      setAssignedBoardUsers(checklist.assignedBoardUsers || []);
      setCanEdit(checklist.canEdit);
      setCanView(checklist.canView);
    }
  }, [checklist.id, isCreateMode, checklist.assignedBoardUsers, checklist.canEdit, checklist.canView]);

  // Listen for phantom checklist to sync with backend
  // This prevents CRUD failures when trying to create items with phantom IDs
  useEffect(() => {
    if (!isWaitingForSync || !phantomChecklistRef.current) return;

    const detacher = checklistStore.on("idChange", (event: { record: unknown; value: number | string }) => {
      if (event.record === phantomChecklistRef.current) {
        // Sync complete - update IDs and enable item creation
        checklist.id = event.value;
        setCurrentChecklistId(event.value);
        setIsTitleSaved(true);
        setIsWaitingForSync(false);
        setTab("items");

        // Clear phantom ref now that sync is complete
        phantomChecklistRef.current = null;

        detacher();
      }
    });

    // Fallback timeout in case sync never completes
    const timeoutId = setTimeout(() => {
      setIsWaitingForSync(false);
      setIsTitleSaved(true);
      phantomChecklistRef.current = null;
      detacher();
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      detacher();
    };
  }, [isWaitingForSync, checklist, checklistStore, setTab]);

  // Checklist title handlers
  const handleChecklistTitleUpdate = () => {
    const trimmedTitle = checklistTitle.trim();
    if (trimmedTitle.length <= 2 || trimmedTitle === checklist.title) return;

    if (isCreateMode) {
      // Create the checklist (initially as phantom record)
      const newChecklist = createChecklist(trimmedTitle) as unknown as Checklist;
      if (!newChecklist) return;

      // Update the checklist prop with phantom data
      Object.assign(checklist, newChecklist);

      // Sync permission state values from the newly created phantom record
      setAssignedBoardUsers(newChecklist.assignedBoardUsers || []);
      setCanEdit(newChecklist.canEdit);
      setCanView(newChecklist.canView);

      // Store ref and trigger sync wait - the useEffect will handle idChange
      phantomChecklistRef.current = newChecklist;
      setIsWaitingForSync(true);
    } else {
      // Existing checklist - just update the title
      updateChecklist(checklist, { title: trimmedTitle });
      setIsTitleSaved(true);
    }
  };

  // Checklist item handlers
  const handleCreateChecklistItem = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent modal dialogs

    const title = checklistItemTitle.trim();
    if (!title) return;

    // Prevent creating items with phantom IDs (not yet synced to backend)
    if (isPhantomId(currentChecklistId)) return;

    // Create item with the current synced checklist ID
    const checklistWithSyncedId = { ...checklist, id: currentChecklistId };
    createChecklistItem(checklistWithSyncedId as Checklist, title);
    setChecklistItemTitle("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleDeleteItem = (item: ChecklistItems) => {
    deleteChecklistItem(item);
  };

  const handleStartEdit = (item: ChecklistItems) => {
    setEditingItemId(item.id);
    setEditingText(item.description);
  };

  const handleSaveEdit = (item: ChecklistItems) => {
    if (editingText.trim() && editingText !== item.description) {
      updateChecklistItem(item, { description: editingText.trim() });
    }
    setEditingItemId(null);
    setEditingText("");
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingText("");
  };

  // Helper to get the actual Bryntum record from the store
  const getStoreRecord = useCallback(() => {
    if (!currentChecklistId) return null;
    return checklistStore.getById(currentChecklistId) as unknown as Checklist;
  }, [currentChecklistId, checklistStore]);

  // Permissions handlers
  const handleAssignedUsersChange = (value: TComboSelectedValue | null) => {
    const users = Array.isArray(value) ? value : [];
    const userIds = users.map(Number);

    // Get the actual record from the store and update it using Bryntum's set method
    const storeRecord = getStoreRecord();
    if (storeRecord) {
      // Use Bryntum's set() method to ensure proper change tracking
      storeRecord.set("assignedBoardUsers", userIds);
    }

    // Update local state for reactive UI
    setAssignedBoardUsers(userIds);
  };

  const handleCanEditChange = (value: TComboSelectedValue | null) => {
    const val = typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
    const numVal = val ? Number(val) : undefined;

    // Get the actual record from the store and update it using Bryntum's set method
    const storeRecord = getStoreRecord();
    if (storeRecord) {
      // Use Bryntum's set() method to ensure proper change tracking
      storeRecord.set("canEdit", numVal);
    }

    // Update local state for reactive UI
    setCanEdit(numVal);
  };

  const handleCanViewChange = (value: TComboSelectedValue | null) => {
    const val = typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
    const numVal = val ? Number(val) : undefined;

    // Get the actual record from the store and update it using Bryntum's set method
    const storeRecord = getStoreRecord();
    if (storeRecord) {
      // Use Bryntum's set() method to ensure proper change tracking
      storeRecord.set("canView", numVal);
    }

    // Update local state for reactive UI
    setCanView(numVal);
  };

  return {
    // Mode & validation
    isCreateMode,
    canAccessOtherTabs,
    isWaitingForSync,

    // Title state & handlers
    checklistTitle,
    setChecklistTitle,
    handleChecklistTitleUpdate,

    // Item creation state & handlers
    checklistItemTitle,
    setChecklistItemTitle,
    handleCreateChecklistItem,
    inputRef,

    // Item editing state & handlers
    editingItemId,
    editingText,
    setEditingText,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteItem,

    // Drag & drop
    visualItems,
    draggedItem,
    droppedItemId,
    scrollContainerRef,
    handleDragStart,
    handleDragOver,
    handleDragEnd,

    // Permissions state & handlers
    assignedBoardUsers,
    canEdit,
    canView,
    handleAssignedUsersChange,
    handleCanEditChange,
    handleCanViewChange
  };
};

export const ChecklistAddItemsModal = ({ checklist }: ChecklistAddItemsModalProps) => {
  const [tab, setTab] = useState(!checklist.title ? "title" : "items");

  // Prevent all interactive events from bubbling to parent dialogs
  // This prevents the MoreMenu dialog from auto-closing when forms are submitted
  const handlePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const {
    // Mode & validation
    canAccessOtherTabs,
    isWaitingForSync,
    // Title
    checklistTitle,
    setChecklistTitle,
    handleChecklistTitleUpdate,
    // Items
    checklistItemTitle,
    setChecklistItemTitle,
    handleCreateChecklistItem,
    inputRef,
    // Editing
    editingItemId,
    editingText,
    setEditingText,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteItem,
    // Drag & drop
    visualItems,
    draggedItem,
    droppedItemId,
    scrollContainerRef,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    // Permissions state & handlers
    assignedBoardUsers,
    canEdit,
    canView,
    handleAssignedUsersChange,
    handleCanEditChange,
    handleCanViewChange
  } = useChecklistModal(checklist, setTab);

  return (
    <div className="pt-6 flex-1 min-h-0 flex flex-col" onPointerDown={handlePointerDown} onClick={handleClick}>
      <Tabs value={tab} onValueChange={setTab} className="w-full flex flex-col flex-1 min-h-0">
        <TabsList className="mb-6 w-full flex-none bg-card border border-border">
          <TabsTrigger value="title" className="flex-1 gap-2">
            <FileEdit className="size-4" />
            Title
          </TabsTrigger>
          <TabsTrigger value="items" className="flex-1 gap-2" disabled={!canAccessOtherTabs}>
            <ListChecks className="size-4" />
            Items
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex-1 gap-2" disabled={!canAccessOtherTabs}>
            <Shield className="size-4" />
            Permissions
          </TabsTrigger>
        </TabsList>
        <section ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto">
          <div className=" rounded-xl border border-border/60 mb-6  p-4 shadow-sm ring-1 ring-black/5 sm:p-6 bg-card">
            <TabsContent value="title" className="py-2">
              <div className="space-y-4">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // Prevent event from bubbling to parent modal dialogs
                    handleChecklistTitleUpdate();
                  }}
                >
                  <Label htmlFor="checklistTitle" className="text-xs text-muted-foreground uppercase block pb-2">
                    Checklist Title
                  </Label>
                  <InputGroup>
                    <InputGroupInput
                      autoFocus
                      value={checklistTitle}
                      onChange={(e) => setChecklistTitle(e.target.value)}
                      id="checklistTitle"
                      placeholder="Enter checklist title..."
                      className="py-4 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ring-0"
                    />
                    <InputGroupAddon align="inline-end">
                      <InputGroupButton type="submit" variant="outline" size="sm">
                        Save
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="items" className="py-2">
              <div className="hidden">
                <p className="text-xs text-muted-foreground">Add items, tap to edit item text, delete items, and drag items to change the order.</p>
              </div>

              {isWaitingForSync && (
                <div className="mb-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-200">
                  Syncing checklist with server... Please wait a moment before adding items.
                </div>
              )}

              <AddChecklistItemForm inputRef={inputRef} value={checklistItemTitle} onChange={setChecklistItemTitle} onSubmit={handleCreateChecklistItem} />

              <div className="mt-4">
                {visualItems.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-muted/30 py-10 text-center text-sm text-muted-foreground"
                  >
                    <ListChecks className="size-5" />
                    No items yet. Add one above to get started.
                  </motion.div>
                ) : (
                  <div>
                    {visualItems.map((item) => {
                      const isDragging = draggedItem?.id === item.id;
                      const justDropped = droppedItemId === item.id;

                      return (
                        <motion.div
                          key={item.id}
                          layoutId={`checklist-item-${item.id}`}
                          layout
                          animate={{
                            opacity: isDragging ? 0.4 : 1,
                            scale: justDropped ? [1, 1, 1] : 1
                          }}
                          transition={{
                            layout: { type: "spring", bounce: 0, duration: 0.2 },
                            opacity: { duration: 0.15 },
                            scale: { duration: 0.3, times: [0, 0.5, 1] }
                          }}
                          draggable={editingItemId !== item.id}
                          onDragStart={() => handleDragStart(item)}
                          onDragOver={(e) => handleDragOver(e, item)}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            "mt-4 group flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-2 py-1 shadow-sm ",
                            editingItemId !== item.id && "cursor-move hover:border-primary/40 hover:bg-muted/10 hover:shadow-md",
                            editingItemId === item.id && "border-primary"
                          )}
                        >
                          <motion.span
                            whileHover={editingItemId !== item.id ? { scale: 1.15 } : {}}
                            transition={{ duration: 0.15 }}
                            className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-md border border-muted-foreground/30 bg-muted/40 text-muted-foreground transition-colors",
                              editingItemId !== item.id && "group-hover:border-primary/40 group-hover:text-primary"
                            )}
                          >
                            <GripVertical className="size-4" />
                          </motion.span>

                          {editingItemId === item.id ? (
                            <div className="flex flex-1 items-center gap-2">
                              <Input
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveEdit(item);
                                  } else if (e.key === "Escape") {
                                    handleCancelEdit();
                                  }
                                }}
                                className="h-9 flex-1"
                                autoFocus
                              />
                              <Button variant="ghost" size="icon" className="size-8 text-success hover:bg-success/10" onClick={() => handleSaveEdit(item)}>
                                <Check className="size-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="size-8 text-destructive hover:bg-destructive/10" onClick={handleCancelEdit}>
                                <X className="size-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-1 items-center gap-3">
                              <button
                                type="button"
                                className="flex-1 cursor-text text-left text-sm leading-5 text-foreground transition-colors hover:text-primary"
                                onClick={() => handleStartEdit(item)}
                              >
                                {item.description}
                              </button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                                onClick={() => handleDeleteItem(item)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="mt-0">
              <PermissionsSection
                assignedBoardUsers={assignedBoardUsers}
                canEdit={canEdit}
                canView={canView}
                onAssignedUsersChange={handleAssignedUsersChange}
                onCanEditChange={handleCanEditChange}
                onCanViewChange={handleCanViewChange}
              />
            </TabsContent>
          </div>
        </section>
      </Tabs>
    </div>
  );
};
